import { useCallback } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Search from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, ButtonProps, Stack } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';

import { Props } from './props';

// ======================= HANDLE SUBMIT BUTTON PROPS
const useSubmitStyle = (label?: string) => {
  switch (label?.toLowerCase()) {
    case 'search':
      return {
        startIcon: <Search />,
        color: 'primary',
      };
      break;
    case 'approve':
      return {
        startIcon: <Check />,
        color: 'success',
      };
      break;
    case 'reject':
      return {
        startIcon: <Close />,
        color: 'error',
      };
      break;
    default:
      return { startIcon: undefined, color: 'primary' };
      break;
  }
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const FormLayout = <T extends FieldValues>(props: Props<T>) => {
  const { t } = useTranslation();
  const {
    submitLabel = `${t('common:submit')}`,
    cancelLabel = `${t('common:cancel')}`,
    exportLabel = `${t('common:download')}`,
    onSubmit,
    onCancel,
    onExport,
    children,
    formOptions,
    submitProps,
    cancelProps,
    exportProps,
    spacing = 6,
    buttonDirection = 'ltr',
    form: formProps,
    submitType,
  } = props;
  const enabledAction = onSubmit || onCancel;
  const { startIcon, color } = useSubmitStyle(submitType);

  // ======================= HOOKS

  const form = useForm<T>(formOptions);
  const formMethods = formProps ?? form;
  const { handleSubmit, reset } = formMethods;

  //======================== FUNCTION
  const onHandleSubmit = useCallback(
    (values: T) => {
      onSubmit?.(values);
    },
    [onSubmit]
  );
  //======================== FUNCTION
  const onHandleExport = useCallback(() => {
    const values = formMethods.getValues();
    onExport?.(values);
  }, [onExport, formMethods]);

  const onHandleReset = useCallback(() => {
    reset();
    onCancel?.();
  }, [reset, onCancel]);

  // ======================= RENDER FUNCTIONS

  const renderActionButtons = () => {
    if (!enabledAction) {
      return;
    }
    return (
      <Stack
        direction={buttonDirection === 'ltr' ? 'row' : 'row-reverse'}
        gap={4}
        flexWrap={'wrap'}
      >
        {onSubmit && (
          <LoadingButton
            type="submit"
            color={color as ButtonProps['color']}
            variant="contained"
            startIcon={startIcon}
            sx={{ width: { xs: '100%', md: 'auto' } }}
            {...submitProps}
          >
            {submitLabel}
          </LoadingButton>
        )}
        {onCancel && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onHandleReset}
            sx={{ width: { xs: '100%', md: 'auto' } }}
            {...cancelProps}
          >
            {cancelLabel}
          </Button>
        )}

        {onExport && (
          <Button
            variant="outlined"
            color="success"
            onClick={onHandleExport}
            startIcon={<FileDownloadOutlinedIcon color="success" />}
            {...exportProps}
            sx={{
              width: { xs: '100%', md: 'auto' },
              ...(exportProps?.sx || {}),
            }}
          >
            {exportLabel}
          </Button>
        )}
      </Stack>
    );
  };

  // ======================= VIEWS
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onHandleSubmit)}>
        <Stack gap={spacing}>
          {children}
          {renderActionButtons()}
        </Stack>
      </form>
    </FormProvider>
  );
};

/**
 * ===========================
 * EXPORT
 * ===========================
 */
export default FormLayout;
